import { Table } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";

// import ExportManager from "./export-manager";
import { ExportManager } from "./ExportManager";

import { Button } from "@/components/ui/button";
import {
  ExtendedColumnDef,
  _TFloatingMenuCustomComponentProps,
} from "@/interfaces/globals";
import { cn } from "@/lib/utils";
import { getSelectedTableRows } from "@/utils/table-utils";

type TProps<TData, TValue> = {
  children?: React.ComponentType<_TFloatingMenuCustomComponentProps>;
  className?: string;
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  table?: Table<TData>;
  isExport?: boolean;
  multiFloating?: {
    multiIds: string[];
    handleUnSelectAll: () => void;
  };
};

export const DataTableActionsFloatingMenu = <D, V>({
  columns,
  data,
  table,
  isExport,
  children,
  multiFloating,
}: TProps<D, V>): React.JSX.Element => {
  const ids = useMemo(() => {
    if (multiFloating) {
      return multiFloating.multiIds;
    } else if (table) {
      return getSelectedTableRows(table);
    } else {
      return [];
    }
  }, [JSON.stringify(table), multiFloating, multiFloating?.multiIds]);

  const [state, setState] = useState(false);

  useEffect(() => {
    if (ids.length > 0) {
      setState(true);
    } else {
      setState(false);
    }
  }, [ids]);

  const Component = children || Fragment;

  const handleClose = (): void => {
    setState(false);
    if (multiFloating) {
      multiFloating?.handleUnSelectAll();

      return;
    }
    table?.setRowSelection({});
  };

  return (
    <div className='fixed bottom-8 z-50 left-1/2 -translate-x-1/2'>
      <div
        data-state={state}
        className={cn(
          "border py-1.5 px-4 rounded-md flex items-center divide-x-2 bg-background transition-all ease-in-out duration-300 data-[state=true]:animate-in data-[state=false]:animate-out data-[state=true]:slide-in-from-bottom-16 data-[state=false]:slide-out-to-bottom-16 data-[state=false]:opacity-0 data-[state=true]:opacity-100 data-[state=false]:size-0 overflow-hidden data-[state=false]:p-0 data-[state=false]:border-0",
        )}
      >
        <div className='flex items-center gap-4 mr-4'>
          <Button size='sm' variant='ghost' onClick={handleClose}>
            <X size={16} />
          </Button>
          <span className='text-sm font-medium text-nowrap'>
            {ids.length} selected
          </span>
        </div>

        <div className='pl-4 flex items-center'>
          {isExport && (
            <ExportManager<D, V> columns={columns} data={data} table={table} />
          )}
          <Component {...(children ? { ids, handleClose } : {})} />
        </div>
      </div>
    </div>
  );
};
